import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Paper, Card, CardContent, Button,
  Chip, CircularProgress, Alert, Divider, IconButton, Tooltip, Stack
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Translate as TranslateIcon,
  VolumeUp as AudioIcon,
  Publish as PublishIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import apiClient from '../../services/apiClient';

const PathDetail = () => {
  const { pathId } = useParams();
  const navigate = useNavigate();

  const [path, setPath] = useState(null);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    fetchPathData();
  }, [pathId]);

  const fetchPathData = async () => {
    setLoading(true);
    try {
      // Get path details
      const pathResponse = await apiClient.admin.getPath(pathId);
      setPath(pathResponse.data.path);

      // Get stories for this path
      const storiesResponse = await apiClient.admin.getPathStories(pathId);
      setStories(storiesResponse.data.stories || []);
    } catch (error) {
      console.error('Error fetching path data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePublishPath = async () => {
    if (!window.confirm('This will publish the path and all stories with translations. Continue?')) {
      return;
    }

    setPublishing(true);
    try {
      await apiClient.admin.publishPath(pathId);
      await fetchPathData();
      alert('Path published successfully!');
    } catch (error) {
      console.error('Error publishing path:', error);
      alert(error.response?.data?.error || 'Error publishing path');
    } finally {
      setPublishing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getTranslationStatus = (story) => {
    const translations = story.translations || [];
    const totalLanguages = 9;

    if (translations.length === 0) {
      return { status: 'error', label: 'No translations', color: 'error' };
    }
    if (translations.length === totalLanguages) {
      return { status: 'success', label: `${translations.length} languages`, color: 'success' };
    }
    return { status: 'warning', label: `${translations.length}/${totalLanguages}`, color: 'warning' };
  };

  const getAudioStatus = (story) => {
    const translations = story.translations || [];
    if (translations.length === 0) return { hasAudio: false, partial: false };

    const withAudio = translations.filter(t => t.has_audio).length;
    return {
      hasAudio: withAudio > 0,
      partial: withAudio > 0 && withAudio < translations.length,
      label: `${withAudio}/${translations.length}`,
      color: withAudio === 0 ? 'error' : withAudio === translations.length ? 'success' : 'warning'
    };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!path) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">Path not found</Alert>
      </Box>
    );
  }

  const totalStories = stories.length;
  const publishedStories = stories.filter(s => s.is_published).length;
  const storiesWithTranslations = stories.filter(s => s.translations && s.translations.length > 0).length;

  return (
    <Box sx={{ pb: 10 }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'flex-start' }}
        justifyContent="space-between"
        sx={{ mb: 3, gap: 2 }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ minWidth: 0 }}>
          <IconButton onClick={() => navigate('/admin/paths')} aria-label="Back to paths" sx={{ mt: 0.25 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1}>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                {path.title}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={path.level} color="primary" size="small" variant="outlined" />
                {path.is_published ? (
                  <Chip icon={<PublishIcon />} label="Published" color="success" size="small" variant="outlined" />
                ) : (
                  <Chip label="Draft" variant="outlined" size="small" />
                )}
              </Box>
            </Stack>
            {path.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                {path.description}
              </Typography>
            )}
          </Box>
        </Stack>

        {/* Action Buttons */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}
        >
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/admin/paths/${pathId}/edit`)}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate(`/admin/paths/${pathId}/matrix`)}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Content Matrix
          </Button>
          {!path.is_published && (
            <Button
              variant="contained"
              color="success"
              startIcon={publishing ? <CircularProgress size={20} /> : <PublishIcon />}
              onClick={handlePublishPath}
              disabled={publishing || storiesWithTranslations === 0}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              {publishing ? 'Publishing...' : 'Publish Path'}
            </Button>
          )}
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        {/* Path Info - Full Width on Mobile */}
        <Grid item xs={12} lg={4}>
          <Grid container spacing={2}>
            {/* Path Information Card */}
            <Grid item xs={12} sm={6} lg={12}>
              <Card variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700 }} gutterBottom>
                    Path Information
                  </Typography>
                  <Divider sx={{ mb: 2, borderColor: 'divider' }} />

                  <Box sx={{ display: 'grid', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Level
                      </Typography>
                      <Typography variant="body1">{path.level}</Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Reference Language
                      </Typography>
                      <Typography variant="body1">
                        {path.reference_language?.toUpperCase() || 'EN'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Created
                      </Typography>
                      <Typography variant="body1">{formatDate(path.created_at)}</Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Updated
                      </Typography>
                      <Typography variant="body1">{formatDate(path.updated_at)}</Typography>
                    </Box>

                    {path.tags && path.tags.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Tags
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {path.tags.map(tag => (
                            <Chip key={tag} label={tag} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Statistics Card */}
            <Grid item xs={12} sm={6} lg={12}>
              <Card variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700 }} gutterBottom>
                    Statistics
                  </Typography>
                  <Divider sx={{ mb: 2, borderColor: 'divider' }} />

                  <Box sx={{ display: 'grid', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Total Stories
                      </Typography>
                      <Typography variant="h5">{totalStories}</Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Published Stories
                      </Typography>
                      <Typography variant="h5" color={publishedStories === totalStories ? 'success.main' : 'text.primary'}>
                        {publishedStories}/{totalStories}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Stories with Translations
                      </Typography>
                      <Typography variant="h5" color={storiesWithTranslations === totalStories ? 'success.main' : 'warning.main'}>
                        {storiesWithTranslations}/{totalStories}
                      </Typography>
                    </Box>

                    {path.translations && (
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Path Translations
                        </Typography>
                        <Typography variant="h5">
                          {path.translations.length} languages
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Stories List - Mobile Friendly Grid */}
        <Grid item xs={12} lg={8}>
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ mb: 3, gap: 2 }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Stories ({totalStories})
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/admin/stories/new?pathId=${pathId}`)}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Add Story
              </Button>
            </Stack>

            {stories.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  No stories in this path yet. Add stories to build the learning journey.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/admin/stories/new?pathId=${pathId}`)}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Add the first story
                </Button>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {stories
                  .sort((a, b) => a.order_index - b.order_index)
                  .map((story) => {
                    const translationStatus = getTranslationStatus(story);
                    const audioStatus = getAudioStatus(story);

                    return (
                      <Grid item xs={12} key={story.story_id}>
                        <Card
                          variant="outlined"
                          onClick={() => navigate(`/admin/stories/${story.story_id}/translations`)}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            cursor: 'pointer',
                            transition: 'box-shadow .15s, border-color .15s, transform .15s',
                            '&:hover': {
                              boxShadow: 3,
                              borderColor: 'primary.main',
                              transform: 'translateY(-2px)'
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Story Header */}
                            <Box sx={{
                              display: 'flex',
                              flexDirection: { xs: 'column', sm: 'row' },
                              justifyContent: 'space-between',
                              alignItems: { xs: 'flex-start', sm: 'center' },
                              gap: 1
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  label={`#${story.order_index}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ minWidth: 45 }}
                                />
                                <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.1rem' } }}>
                                  {story.title}
                                </Typography>
                              </Box>

                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                {/*<Tooltip title="Manage Translations">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => navigate(`/admin/stories/${story.story_id}/translations`)}
                                  >
                                    <TranslateIcon />
                                  </IconButton>
                                </Tooltip>*/}
                                <Tooltip title="Open story production">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => { e.stopPropagation(); navigate(`/admin/stories/${story.story_id}/translations`); }}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                {/**<Tooltip title="Edit Story">
                                  <IconButton
                                    size="small"
                                    onClick={() => navigate(`/admin/stories/${story.story_id}/edit`)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>**/}
                              </Box>
                            </Box>

                            {/* Story Stats */}
                            <Box sx={{
                              display: 'flex',
                              gap: 1,
                              flexWrap: 'wrap',
                              alignItems: 'center'
                            }}>
                              <Chip
                                label={`Difficulty: ${story.difficulty_rating || 1}`}
                                size="small"
                                variant="outlined"
                              />

                              <Chip
                                icon={<TranslateIcon sx={{ fontSize: 16 }} />}
                                label={translationStatus.label}
                                size="small"
                                color={translationStatus.color}
                                variant={translationStatus.status === 'error' ? 'outlined' : 'filled'}
                              />

                              {audioStatus.hasAudio && (
                                <Chip
                                  icon={<AudioIcon sx={{ fontSize: 16 }} />}
                                  label={audioStatus.label}
                                  size="small"
                                  color={audioStatus.color}
                                  variant={audioStatus.partial ? 'outlined' : 'filled'}
                                />
                              )}

                              {story.is_published ? (
                                <Chip
                                  icon={<PublishIcon sx={{ fontSize: 16 }} />}
                                  label="Published"
                                  color="success"
                                  size="small"
                                />
                              ) : (
                                <Chip
                                  label="Draft"
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                        </Card>
                      </Grid>
                    );
                  })}
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PathDetail;
